import {
  CustomPagination,
  CustomShipmentText,
  PageHeaderActions,
} from '@/components';
import { TimePeriodPills } from '@/pages/AiHistory/components';
import { ChartDataItem } from '@/pages/AiHistory/type';
import { Col, Flex, Row } from 'antd';
import { useMemo } from 'react';
import LogiblocksStats from './components/LogiblocksStats';
import LogiblocksDashboardTable from './components/LogiblocksDashboardTable';
import type { WorkflowStats } from '@/apis/logiblocksAPI';

interface Team {
  color: string | null;
  name: string;
}

export interface Workflow {
  last_run?: number;
  runs?: number;
  success_rate?: string;
  failures?: number;
  avg_run_time?: string;
  last_updated?: string;
  created_on?: string;
  created_by?: string;
  teams?: Team[];
  is_active?: boolean;
  id: string;
  name: string | null;
  is_draft: boolean;
}

interface LogiblockDashboardProps {
  workflows: Workflow[];
  stats: WorkflowStats | null;
  onWorkflowClick: (workflowId: string) => void;
  maxPageSize: number;
  totalPages: number;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  setSerachInList: React.Dispatch<React.SetStateAction<string>>;
  pageSize: number;
  onNewWorkflow: () => void;
  loading: boolean;
  period: string | null;
  setPeriod: React.Dispatch<React.SetStateAction<string | null>>;
  onToggleLiveness: (workflowId: string, currentlyDraft: boolean) => void;
  /** Called when the user clicks a "Duplicate workflow" button. */
  onDuplicateWorkflow: (workflowId: string) => void;
  /** Workflows whose duplicate is still in progress; their button is disabled. */
  duplicatingWorkflowIds: Set<string>;
}
const LogiblockDashboard: React.FC<LogiblockDashboardProps> = ({
  onWorkflowClick,
  workflows,
  stats,
  maxPageSize,
  totalPages,
  page,
  setPage,
  setSerachInList,
  pageSize,
  onNewWorkflow,
  loading,
  period,
  setPeriod,
  onToggleLiveness,
  onDuplicateWorkflow,
  duplicatingWorkflowIds,
}) => {
  const kpiData: ChartDataItem[] = useMemo(
    () => [
      {
        title: 'Total runs (count)',
        value: stats ? String(stats.total_runs) : null,
        unit: '',
        change: stats?.total_runs_change ?? null,
        is_negative: stats?.total_runs_is_negative ?? false,
        type: 1,
      },
      {
        title: 'Success rate',
        value: stats?.success_rate != null ? String(stats.success_rate) : null,
        unit: '%',
        change: stats?.success_rate_change ?? null,
        is_negative: stats?.success_rate_is_negative ?? false,
        type: 2,
      },
      {
        title: 'Failures (count)',
        value: stats ? String(stats.failures) : null,
        unit: '',
        change: stats?.failures_change ?? null,
        is_negative: stats?.failures_is_negative ?? false,
        type: 1,
      },
      {
        title: 'Run time (avg)',
        value: stats?.avg_run_time ?? null,
        unit: '',
        change: stats?.avg_run_time_change ?? null,
        is_negative: stats?.avg_run_time_is_negative ?? false,
        type: 3,
      },
      {
        title: 'Estimated time saved',
        value: stats?.total_run_time ?? null,
        unit: '',
        change: stats?.total_run_time_change ?? null,
        is_negative: stats?.total_run_time_is_negative ?? false,
        type: 3,
      },
    ],
    [stats]
  );

  const handlePageChange = (newPage: number) => {
    setPage(() => {
      const validPage = newPage > 0 ? newPage : 1;
      return validPage;
    });
  };

  const handleClearSearch = () => {
    setSerachInList('');
  };

  const handleSearch = (data: string) => {
    setSerachInList(data);
    setPage(1);
  };

  const pagedWorkflows = useMemo(
    () => workflows.slice((page - 1) * pageSize, page * pageSize),
    [workflows, page, pageSize]
  );

  const totalCount = maxPageSize;
  const rangeStart = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = totalCount === 0 ? 0 : Math.min(page * pageSize, totalCount);

  return (
    <>
      <Flex vertical gap={16}>
        <CustomShipmentText
          text={'Automations'}
          lineHeight='32px'
          fontSize='24px'
          fontWeight={700}
        />
        <TimePeriodPills
          selectedPeriod={period}
          onChange={(val) => {
            setPeriod(val);
            setPage(1);
          }}
        />
        <LogiblocksStats kpiData={kpiData} />
        <Flex
          className='settings-content-wrapper'
          vertical
          style={{ marginTop: '8px' }}
          gap={16}
        >
          <PageHeaderActions
            title={'Workflows'}
            showToggle={false}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            onSearchChange={handleSearch}
            onSearchClear={handleClearSearch}
            totalCount={totalCount}
            showCount={true}
            showAddButton={true} //need to add logiblocks permissions
            onAddClick={onNewWorkflow}
            showSearch={true}
            addButtonText='Create new'
            addButtonWidth={95}
          ></PageHeaderActions>
          {!loading ? (
            <LogiblocksDashboardTable
              workflowList={pagedWorkflows}
              onWorkflowClick={onWorkflowClick}
              onToggleLiveness={onToggleLiveness}
              onDuplicateWorkflow={onDuplicateWorkflow}
              duplicatingWorkflowIds={duplicatingWorkflowIds}
            />
          ) : (
            <Flex align='center' justify='center' style={{ marginTop: '20px' }}>
              <p className='font-16 fw-600'>Loading workflows...</p>
            </Flex>
          )}
        </Flex>
        <Row
          style={{ width: '100%', marginTop: '20px', paddingBottom: '40px' }}
          justify='center'
        >
          <Col xs={24} className='flex-to-block'>
            <div>
              {workflows.length > 0 && (
                <Flex className='pagination-alignment'>
                  <CustomPagination
                    page={page}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                  />
                </Flex>
              )}
            </div>
          </Col>
        </Row>
      </Flex>
    </>
  );
};

export default LogiblockDashboard;
